import React, { useState } from 'react';
import { CreditCard } from 'lucide-react';

import {
  ACCOUNT_PLAN,
  ACCOUNT_PLAN_INCLUDES,
  ACCOUNT_PLAN_PRICE,
  ACCOUNT_RENEWAL,
  ACCOUNT_SITE_PRICES,
} from './accountFixture';
import { accountMoney } from './accountModel';
import { useAccountRecord } from './AccountRecordContext';
import { AccountPageShell } from './AccountPageShell';
import { AddressLines } from './AccountPersonal';
import {
  AccountCard,
  CardColumn,
  CheckField,
  Chip,
  FieldPair,
  Grid,
  NotYetButton,
  Row,
  ScopeChip,
  SmallButton,
  StatusChip,
  Table,
  TextField,
} from './primitives';
import { isAccountOwner } from './accountModel';

/**
 * AR-380 — Plan + Agreements, and Billing.
 *
 * v15 changed who sees what. Billing is still the Owner's, but Plan +
 * Agreements is everyone's: an associate advisor sees a REDUCED page — her
 * status on the owner's account, her own agreement and Industry credentials,
 * with no plan, price, renewal, commission table or comparison (D-ACCT-40).
 *
 * Industry credentials became two cards: WorldVia's own fixed numbers, each
 * with a Copy, and the advisor's own, which are unverified and carry no status
 * chip (D-ACCT-41). "What your plan includes" is gone (D-ACCT-34), and billing
 * history lists every charge on the account, not only subscriptions (D-ACCT-42).
 */

/** WorldVia's own numbers — the same for every member, so each one copies. */
const WORLDVIA_CREDENTIALS: readonly (readonly [string, string])[] = [
  ['IATA / ARC', '24632716'],
  ['Cruise line booking phone', '763-497-0312'],
  ['Seller of Travel — California', 'CST 2121001-50'],
  ['Seller of Travel — Florida', 'ST42403'],
  ['Seller of Travel — Washington', 'WA UBI 603 473 359'],
];

/**
 * The advisor's own, which WorldVia does not verify — so no status chip.
 *
 * AR-381: none of the three has a column (AR-392), so on a live account they
 * are all blank. The sample's CLIA number is another member's, and an
 * industry credential is exactly the kind of value a member would copy into
 * a booking without checking — a footer saying "sample" does not undo that.
 */
const OWN_CREDENTIALS: readonly (readonly [string, string])[] = [
  ['CLIA', '00412988'],
  ['IATAN', '—'],
  ['State Seller of Travel licence', '—'],
];
const ownCredentials = (isLive: boolean): readonly (readonly [string, string])[] =>
  isLive ? OWN_CREDENTIALS.map(([label]) => [label, '—'] as const) : OWN_CREDENTIALS;

const CopyRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const [copied, setCopied] = React.useState(false);
  return (
    <Row
      label={label}
      value={value}
      control={
        <SmallButton
          onClick={() => {
            void navigator.clipboard?.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </SmallButton>
      }
    />
  );
};

/** The plan is one fact in three places, so it is written once. */
const useSubscriptions = () => {
  const { record, billing } = useAccountRecord();
  // AR-381: when Stripe has answered, THAT is the list — a member holds one
  // subscription per product, so the page prints what Stripe bills rather
  // than deriving a membership row from a tier name and a Sites row from a
  // mode flag. With no Stripe answer (an associate, or an environment with no
  // key) the sample stands and the reason is on the payload's `unbound` list.
  if (billing) {
    return billing.subscriptions.map((x) => ({
      product: x.prod,
      plan: x.plan,
      price: x.price,
      cycle: x.cycle,
      status: x.status,
      description: x.desc,
    }));
  }
  const subs = [
    {
      product: `${ACCOUNT_PLAN} membership`,
      plan: ACCOUNT_PLAN,
      price: ACCOUNT_PLAN_PRICE,
      cycle: 'Monthly',
      status: 'Active',
      description: `${ACCOUNT_PLAN} membership, monthly`,
    },
  ];
  // Sites + Marketing treats mode 'ext' as having no Sites account, so there is
  // no Sites subscription to bill and no billing history for one either.
  if (record.web.mode === 'sites') {
    subs.push({
      product: 'WorldVia Sites',
      plan: record.web.plan,
      price: ACCOUNT_SITE_PRICES[record.web.plan] ?? 0,
      cycle: 'Monthly',
      status: 'Active',
      description: `WorldVia Sites, ${record.web.plan}, monthly`,
    });
  }
  return subs;
};

/** The credential rows Membership and Documents both read. */
export const ACCOUNT_CREDENTIALS: readonly (readonly [
  string,
  string,
  string,
  string,
])[] = [
  ['IATA', '—', 'Provided through WorldVia', 'Current'],
  ['ARC', '—', 'Provided through WorldVia', 'Current'],
  ['CLIA', '00412988', 'Belongs to your business', 'Current'],
  ['IATAN', '—', 'Belongs to you personally', 'Not on file'],
];

export const AccountMembership: React.FC = () => {
  const { record, role, billing, viewer, isLive } = useAccountRecord();
  const subs = useSubscriptions();
  const plan = subs[0];
  const c = record.comm;
  const owner = viewer ? viewer.isOwner : isAccountOwner(role);
  // A member on a free plan is billed nothing, and the row must not show them
  // a catalogue price (D-ACCT-40).
  const planName = billing ? billing.plan : plan.plan;
  const planPrice = billing ? billing.price : plan.price;
  const planCycle = billing ? billing.cycle : plan.cycle;
  const renewal = billing ? billing.renewal : ACCOUNT_RENEWAL;

  return (
    <AccountPageShell page="membership">
      {owner ? (
        <>
          <div className="max-w-[720px] rounded-lg border border-gray-200 bg-white px-4 py-3.5 dark:border-gray-700 dark:bg-[#1A2021]">
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
                Membership
              </span>
              <StatusChip status="Active" />
            </div>
            <Row label="Plan" value={planName} />
            <Row
              label="Membership price"
              value={billing?.free ? 'Included' : `${accountMoney(planPrice)} / mo`}
            />
            <Row label="Billing frequency" value={planCycle} />
            <Row label="Next renewal" value={renewal} last />
            <div className="mt-2.5 flex gap-2">
              <NotYetButton note="The plan catalog moves into the platform with the build">
                Compare plans
              </NotYetButton>
              <NotYetButton note="Member Success walks a plan change through with you">
                Change plan
              </NotYetButton>
            </div>
          </div>

          {/* The ACTUAL rates, by booking type, never a plan default. */}
          <div className="mt-3.5 max-w-[720px] rounded-lg border border-gray-200 bg-white px-4 py-3.5 dark:border-gray-700 dark:bg-[#1A2021]">
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
                Commission
              </span>
              <ScopeChip scope="business" />
            </div>
            <Table
              columns={['Booking type', 'Your share']}
              rows={[
                [
                  <b className="text-gray-900 dark:text-gray-100">Non-air bookings</b>,
                  `${c.nonair}%`,
                ],
                [
                  <b className="text-gray-900 dark:text-gray-100">Air bookings</b>,
                  `${c.air}%`,
                ],
                [
                  <b className="text-gray-900 dark:text-gray-100">Service fees</b>,
                  `${c.fees}%`,
                ],
              ]}
            />
            <Row
              label="Plan addendum"
              control={
                <NotYetButton note="The addendum is generated in WorldVia PRO until billing moves into the platform">
                  PDF
                </NotYetButton>
              }
              last
            />
          </div>
        </>
      ) : (
        /* An associate is on the owner's account: her status, not a plan. */
        <div className="max-w-[720px] rounded-lg border border-gray-200 bg-white px-4 py-3.5 dark:border-gray-700 dark:bg-[#1A2021]">
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
              Membership
            </span>
            <StatusChip status="Active" />
          </div>
          <Row label="Your status" value="Associate Advisor" />
          <Row
            label="On the account of"
            value={record.biz.brand}
            extra="Your plan, billing and commission terms are the account owner's."
            last
          />
        </div>
      )}

      <AccountCard name="Agreements" className="mt-3.5 max-w-[720px]">
        <Row
          label={owner ? 'Service agreement' : 'Associate agreement'}
          value="Signed Mar 12, 2024"
          chip={<StatusChip status="Signed" />}
          control={
            <NotYetButton note="Signed copies come to your email until the file store moves into the platform">
              PDF
            </NotYetButton>
          }
          last={!owner}
        />
        {owner ? (
          <Row
            label="Associate addendum"
            value="Signed Mar 12, 2024"
            chip={<StatusChip status="Signed" />}
            control={
              <NotYetButton note="Signed copies come to your email until the file store moves into the platform">
                PDF
              </NotYetButton>
            }
            last
          />
        ) : null}
      </AccountCard>

      {/* Two cards: WorldVia's own numbers, and the advisor's (D-ACCT-41). */}
      <Grid cols={2} className="mt-3.5">
        <AccountCard name="Industry credentials — WorldVia">
          {WORLDVIA_CREDENTIALS.map(([label, value]) => (
            <CopyRow key={label} label={label} value={value} />
          ))}
          <Row
            label=""
            value="These are WorldVia's numbers, the same for every member."
            last
          />
        </AccountCard>
        <AccountCard name="Industry credentials — yours">
          {/* Unverified, so no status chip claims otherwise. */}
          {ownCredentials(isLive).map(([label, value], i) => (
            <Row
              key={label}
              label={label}
              value={value}
              last={i === OWN_CREDENTIALS.length - 1}
            />
          ))}
          <div className="mt-2.5">
            <NotYetButton note="Credential upload lands with the file store — Member Success records it for you today">
              Add a credential
            </NotYetButton>
          </div>
        </AccountCard>
      </Grid>
    </AccountPageShell>
  );
};

// ---------------------------------------------------------------------------

/**
 * Billing history lists EVERY charge on the account, not only the recurring
 * subscriptions (D-ACCT-42) — a one-off sits in the same table as a renewal,
 * because a member looking for "what did WorldVia charge me" does not know
 * which kind it was.
 */
const BILLING_DATES = ['Jul 1, 2026', 'Jun 1, 2026', 'May 1, 2026'];

/** Charges that are not a subscription renewal. */
const ONE_OFF_CHARGES: readonly (readonly [string, string, number])[] = [
  ['Jun 18, 2026', 'Marketing — printed brochures', 84],
  ['May 22, 2026', 'Event ticket — Regional Summit', 149],
];

export const AccountBilling: React.FC = () => {
  const { record, isOpen, draftValue, billing } = useAccountRecord();
  const subs = useSubscriptions();
  const b = record.bill;
  const [, repaintAddress] = useState(0);
  const same = isOpen('bill') ? Boolean(draftValue('bill.same')) : b.same;
  // The card on file is Stripe's, never a column: `users` carries the
  // customer and subscription ids and nothing else about the payment method.
  const card = billing?.card ?? { brand: b.card, last4: b.last4, exp: b.exp };

  // AR-381 — every charge on the Stripe CUSTOMER, which is already what this
  // page means by "every charge on the account" (D-ACCT-42): a subscription
  // query would list the membership and drop the one-off charges below.
  // Without a Stripe answer the sample months stand.
  const history = billing
    ? billing.history.map(
        (c) => [c.date, c.desc, accountMoney(c.amount), c.status] as const,
      )
    : [
        ...BILLING_DATES.flatMap((date) =>
          subs.map((s) => [date, s.description, accountMoney(s.price), 'Paid'] as const),
        ),
        ...ONE_OFF_CHARGES.map(
          ([date, what, amount]) => [date, what, accountMoney(amount), 'Paid'] as const,
        ),
      ];

  return (
    <AccountPageShell page="billing">
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3.5 dark:border-gray-700 dark:bg-[#1A2021]">
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
            Subscriptions
          </span>
          <StatusChip status="Active" />
        </div>
        <Table
          columns={['Product', 'Plan', 'Price', 'Billing', 'Status']}
          rows={subs.map((s) => [
            <b className="text-gray-900 dark:text-gray-100">{s.product}</b>,
            s.plan,
            accountMoney(s.price),
            s.cycle,
            <StatusChip status={s.status} />,
          ])}
        />
      </div>

      <Grid cols={2} className="mt-3.5">
        <AccountCard
          cardKey="bill"
          name="Payment method"
          chip={<ScopeChip scope="personal" />}
          form={
            <>
              <CheckField
                path="bill.same"
                label="Billing address is the same as my mailing address"
                rerender
                onToggle={() => repaintAddress((n) => n + 1)}
              />
              {same ? null : (
                <>
                  <TextField path="bill.street" label="Street" />
                  <FieldPair>
                    <TextField path="bill.city" label="City" />
                    <TextField path="bill.state" label="State" />
                  </FieldPair>
                  <TextField path="bill.zip" label="ZIP" />
                </>
              )}
            </>
          }
        >
          <div className="flex items-center gap-3 border-b border-gray-100 py-2.5 dark:border-gray-800">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              <CreditCard className="h-4 w-4" />
            </span>
            <b className="min-w-0 flex-1 font-semibold text-gray-900 dark:text-gray-100">
              {card.brand} ···· {card.last4}
            </b>
          </div>
          <Row label="Expires" value={card.exp} />
          <Row
            label="Billing address"
            value={<AddressLines address={b.same ? record.addr : b} />}
            chip={b.same ? <Chip>same as mailing address</Chip> : undefined}
            last
          />
          <div className="mt-2.5">
            <NotYetButton note="Billing moves into the platform with the build — Member Success updates the card meanwhile">
              Update payment method
            </NotYetButton>
          </div>
        </AccountCard>
        <div />
      </Grid>

      <div className="mt-3.5 rounded-lg border border-gray-200 bg-white px-4 py-3.5 dark:border-gray-700 dark:bg-[#1A2021]">
        <div className="mb-3">
          <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
            Billing history
          </span>
        </div>
        <Table
          columns={['Date', 'Description', 'Amount', 'Status', 'Receipt']}
          rows={history.map(([date, description, amount, status]) => [
            <b className="text-gray-900 dark:text-gray-100">{date}</b>,
            description,
            amount,
            <StatusChip status={status} />,
            <NotYetButton note="Receipts come to your email until the file store moves into the platform">
              Receipt (PDF)
            </NotYetButton>,
          ])}
        />
      </div>
    </AccountPageShell>
  );
};
